import * as THREE from 'three';

export class ThreeScene {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private canvas: HTMLCanvasElement;
  private animationId: number | null = null;
  private mouseX = 0;
  private mouseY = 0;
  private nodes: THREE.Mesh[] = [];
  private connections: THREE.Line[] = [];
  private time = 0;

  constructor(canvasId: string) {
    this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;

    // Scene
    this.scene = new THREE.Scene();

    // Camera
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.z = 30;

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);

    // Event listeners
    window.addEventListener('resize', this.onResize.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));

    // Create network
    this.createNetwork();

    // Start animation
    this.animate();
  }

  private createNetwork(): void {
    const nodeCount = 50;
    const spread = 40;

    // Create nodes
    const nodeGeometry = new THREE.SphereGeometry(0.2, 16, 16);
    const nodeMaterial = new THREE.MeshBasicMaterial({
      color: 0x00FFFF,
      transparent: true,
      opacity: 0.8
    });

    for (let i = 0; i < nodeCount; i++) {
      const node = new THREE.Mesh(nodeGeometry, nodeMaterial.clone());
      node.position.x = (Math.random() - 0.5) * spread;
      node.position.y = (Math.random() - 0.5) * spread;
      node.position.z = (Math.random() - 0.5) * spread;

      // Store original position for animation
      node.userData.originalPosition = node.position.clone();
      node.userData.speed = Math.random() * 0.5 + 0.5;
      node.userData.offset = Math.random() * Math.PI * 2;

      this.nodes.push(node);
      this.scene.add(node);
    }

    // Create connections between nearby nodes
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x00AFFF,
      transparent: true,
      opacity: 0.2
    });

    for (let i = 0; i < this.nodes.length; i++) {
      for (let j = i + 1; j < this.nodes.length; j++) {
        const distance = this.nodes[i].position.distanceTo(this.nodes[j].position);
        if (distance < 10) {
          const geometry = new THREE.BufferGeometry().setFromPoints([
            this.nodes[i].position,
            this.nodes[j].position
          ]);
          const line = new THREE.Line(geometry, lineMaterial.clone());
          line.userData.nodeA = i;
          line.userData.nodeB = j;
          this.connections.push(line);
          this.scene.add(line);
        }
      }
    }

    // Add ambient glow
    const ambientLight = new THREE.AmbientLight(0x00FFFF, 0.1);
    this.scene.add(ambientLight);
  }

  private onResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  private onMouseMove(event: MouseEvent): void {
    this.mouseX = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  private animate(): void {
    this.animationId = requestAnimationFrame(this.animate.bind(this));

    this.time += 0.01;

    // Animate nodes
    this.nodes.forEach((node, i) => {
      const original = node.userData.originalPosition;
      const speed = node.userData.speed;
      const offset = node.userData.offset;

      node.position.x = original.x + Math.sin(this.time * speed + offset) * 0.5;
      node.position.y = original.y + Math.cos(this.time * speed + offset) * 0.5;
      node.position.z = original.z + Math.sin(this.time * speed * 0.5 + offset) * 0.3;

      // Pulse effect
      const scale = 1 + Math.sin(this.time * 2 + i) * 0.2;
      node.scale.setScalar(scale);

      // Update opacity based on distance from camera
      const distance = node.position.distanceTo(this.camera.position);
      const material = node.material as THREE.MeshBasicMaterial;
      material.opacity = Math.max(0.3, 1 - distance / 50);
    });

    // Update connection lines
    this.connections.forEach(line => {
      const nodeA = this.nodes[line.userData.nodeA];
      const nodeB = this.nodes[line.userData.nodeB];

      const positions = new Float32Array([
        nodeA.position.x, nodeA.position.y, nodeA.position.z,
        nodeB.position.x, nodeB.position.y, nodeB.position.z
      ]);

      line.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      line.geometry.attributes.position.needsUpdate = true;

      // Update line opacity based on distance
      const distance = nodeA.position.distanceTo(nodeB.position);
      const material = line.material as THREE.LineBasicMaterial;
      material.opacity = Math.max(0.05, 0.3 - distance / 30);
    });

    // Camera follows mouse slightly
    this.camera.position.x += (this.mouseX * 3 - this.camera.position.x) * 0.02;
    this.camera.position.y += (this.mouseY * 3 - this.camera.position.y) * 0.02;
    this.camera.lookAt(0, 0, 0);

    this.renderer.render(this.scene, this.camera);
  }

  public dispose(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    window.removeEventListener('resize', this.onResize.bind(this));
    window.removeEventListener('mousemove', this.onMouseMove.bind(this));

    this.nodes.forEach(node => {
      node.geometry.dispose();
      (node.material as THREE.Material).dispose();
    });

    this.connections.forEach(line => {
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    });

    this.renderer.dispose();
  }
}

// Neural network visualization for technology section
export class NeuralNetworkViz {
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private container: HTMLElement | null;
  private animationId: number | null = null;
  private layers: THREE.Group[] = [];
  private time = 0;

  constructor(containerId: string) {
    this.container = document.getElementById(containerId);
    if (!this.container) return;

    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    // Scene
    this.scene = new THREE.Scene();

    // Camera
    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    this.camera.position.z = 15;

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.container.appendChild(this.renderer.domElement);

    this.createNeuralNetwork();
    this.animate();
  }

  private createNeuralNetwork(): void {
    const layerConfigs = [4, 8, 12, 8, 4];
    const layerSpacing = 4;
    const nodeSpacing = 1.5;

    const nodeGeometry = new THREE.SphereGeometry(0.15, 16, 16);

    layerConfigs.forEach((nodeCount, layerIndex) => {
      const layer = new THREE.Group();
      const xPos = (layerIndex - (layerConfigs.length - 1) / 2) * layerSpacing;

      for (let i = 0; i < nodeCount; i++) {
        const yPos = (i - (nodeCount - 1) / 2) * nodeSpacing;

        const hue = 0.5 + layerIndex * 0.1;
        const color = new THREE.Color().setHSL(hue, 1, 0.5);

        const material = new THREE.MeshBasicMaterial({
          color: color,
          transparent: true,
          opacity: 0.8
        });

        const node = new THREE.Mesh(nodeGeometry, material);
        node.position.set(xPos, yPos, 0);
        node.userData.baseY = yPos;
        node.userData.index = i;
        layer.add(node);
      }

      this.layers.push(layer);
      this.scene.add(layer);
    });

    // Create connections between layers
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x00AFFF,
      transparent: true,
      opacity: 0.1
    });

    for (let l = 0; l < this.layers.length - 1; l++) {
      const currentLayer = this.layers[l];
      const nextLayer = this.layers[l + 1];

      currentLayer.children.forEach(nodeA => {
        nextLayer.children.forEach(nodeB => {
          if (Math.random() > 0.5) return; // Random connections

          const points = [
            nodeA.position.clone(),
            nodeB.position.clone()
          ];

          const geometry = new THREE.BufferGeometry().setFromPoints(points);
          const line = new THREE.Line(geometry, lineMaterial.clone());
          this.scene.add(line);
        });
      });
    }
  }

  private animate(): void {
    this.animationId = requestAnimationFrame(this.animate.bind(this));

    this.time += 0.02;

    // Animate layers
    this.layers.forEach((layer, layerIndex) => {
      layer.children.forEach((node, nodeIndex) => {
        const mesh = node as THREE.Mesh;
        const baseY = mesh.userData.baseY;

        mesh.position.y = baseY + Math.sin(this.time + layerIndex + nodeIndex * 0.5) * 0.2;

        const scale = 1 + Math.sin(this.time * 2 + nodeIndex) * 0.1;
        mesh.scale.setScalar(scale);
      });
    });

    // Rotate entire scene slightly
    this.scene.rotation.y = Math.sin(this.time * 0.3) * 0.1;

    this.renderer.render(this.scene, this.camera);
  }

  public dispose(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.renderer) {
      this.renderer.dispose();
      if (this.container) {
        this.container.removeChild(this.renderer.domElement);
      }
    }
  }
}
